import type { TiltScoutSnapshot } from "./tilt-scout-types";

const FUSE_HEIGHT_SCALE = 0.82;

export const ARROW_SIZE_PX = 15;
export const ARROW_SLOT_WIDTH_PX = 24;
export const ARROW_FUSE_GAP_PX = 3;
export const FUSE_WIDTH_PX = 112;
export const FUSE_HEIGHT_PX = 16 * FUSE_HEIGHT_SCALE;
export const DOT_WIDTH_PX = 20;
export const DOT_HEIGHT_PX = 9;
export const DOT_THRESHOLD = 1.5;

export function postureColor(posture: TiltScoutSnapshot["posture"]) {
  if (posture === "bullish") return "var(--fintheon-bullish)";
  if (posture === "bearish") return "var(--fintheon-bearish)";
  return "var(--fintheon-tilt)";
}

export function segmentClip(
  side: "left" | "right",
  tipPx: number,
  strength: number,
  morphStrength: number,
) {
  if (strength > 0.96 || morphStrength < 0.08) return "none";
  const pinch = Math.round(tipPx * (0.35 + morphStrength * 0.65));
  return side === "left"
    ? `polygon(${pinch}px 0, 100% 0, 100% 100%, ${pinch}px 100%, 0 50%)`
    : `polygon(0 0, calc(100% - ${pinch}px) 0, 100% 50%, calc(100% - ${pinch}px) 100%, 0 100%)`;
}

export function activeRadius(
  side: "left" | "right",
  morphStrength: number,
  strength: number,
) {
  if (strength > 0.96) {
    return side === "left" ? "999px 0 0 999px" : "0 999px 999px 0";
  }
  if (morphStrength < 0.12) return 999;
  const centerRadius = Math.round(12 * (1 - morphStrength));
  return side === "left"
    ? `999px ${centerRadius}px ${centerRadius}px 999px`
    : `${centerRadius}px 999px 999px ${centerRadius}px`;
}

export function dotFill(color: string) {
  return `color-mix(in srgb, ${color} 78%, #050402)`;
}

export function fuseContainerBackground() {
  return "linear-gradient(180deg, rgba(240,234,214,0.055) 0%, rgba(240,234,214,0.012) 23%, rgba(0,0,0,0.54) 54%, rgba(0,0,0,0.94) 100%), linear-gradient(90deg, rgba(0,0,0,0.94) 0%, color-mix(in srgb, var(--fintheon-bg) 88%, var(--fintheon-muted) 12%) 50%, rgba(0,0,0,0.94) 100%)";
}

export function fuseContainerShadow() {
  return "inset 0 0 0 1px rgba(63,63,70,0.42), inset 0 1px 0 rgba(80,80,86,0.22), inset 0 -1px 0 rgba(0,0,0,0.98), inset 0 0 0 2px rgba(0,0,0,0.72), inset 0 0 8px 1px rgba(0,0,0,0.92)";
}

export function lavaInsetShadow(color: string) {
  return `inset 0 1px 1px rgba(240,234,214,0.12), inset 0 -1px 2px rgba(0,0,0,0.58), inset 0 0 5px color-mix(in srgb, ${color} 34%, transparent)`;
}

export function dotHairline(color: string) {
  return `0 0 0 1px color-mix(in srgb, ${color} 18%, transparent)`;
}

export function morphValue(magnitude: number) {
  return clampFuseValue((magnitude - DOT_THRESHOLD) / 8.5, 0, 1);
}

function clampFuseValue(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}
